export type PortalPassRules = {
  kind: "visits" | "period" | "minutes";
  validityDays: number;
  validityMode: "relative" | "fixed";
  fixedStartsOn: string | null;
  fixedEndsOn: string | null;
  activationMode: "purchase" | "first_use";
  visitMinutes: number | null;
  serviceUuids: string[];
  branchUuids: string[];
  shareable: boolean;
};

export type PortalPass = {
  uuid: string;
  offerUuid: string;
  ownedByCustomer: boolean;
  visitorName: string;
  offerName: string;
  kind: PortalPassRules["kind"];
  status: "pending" | "scheduled" | "active" | "frozen" | "expired" | "exhausted" | "cancelled";
  paymentStatus: "unpaid" | "paid";
  quantity: number | null;
  remainingQuantity: number | null;
  reservedQuantity: number;
  availableQuantity: number | null;
  validFrom: string | null;
  validUntil: string | null;
  activationDeadline: string;
  beneficiaries: Array<{ customerUuid: string; name: string }>;
  cardCode: string;
  rules: PortalPassRules;
};

export type PortalPassOffer = PortalPassRules & {
  uuid: string;
  name: string;
  description: string | null;
  quantity: number | null;
  activationDeadlineDays: number;
  priceMinor: number;
  currency: string;
  toleranceMinutes: number;
  overtimeBlockMinutes: number;
  overtimePriceMinor: number;
  active: boolean;
  publiclyAvailable: boolean;
};

export type PortalPassOrder = {
  uuid: string;
  reference: string;
  offerUuid: string;
  offerName: string;
  passUuid: string;
  status: "pending" | "paid" | "cancelled";
  amountMinor: number;
  currency: string;
  createdAt: string;
  renewalPassUuid: string | null;
};

export type PortalPasses = { passes: PortalPass[]; offers: PortalPassOffer[]; orders: PortalPassOrder[] };

export type PortalPassDetail = {
  cardImageData: string;
  detail: {
    pass: PortalPass;
    movements: Array<{ uuid: string; kind: string; quantity: number; reason: string | null; createdAt: string }>;
    visits: Array<{
      uuid: string; checkedInAt: string; checkedOutAt: string | null;
      beneficiaryName: string | null; status: string; durationMinutes: number | null;
      overtimeAmountMinor: number; currency: string;
    }>;
  };
};

export function eligibleBookingPasses(passes: PortalPass[], serviceUuid: string | null, startAt: string, endAt: string): PortalPass[] {
  const start = Date.parse(startAt);
  const end = Date.parse(endAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  return passes.filter((pass) => {
    if (pass.paymentStatus !== "paid" || !["active", "pending", "scheduled"].includes(pass.status)) return false;
    if (pass.validFrom && Date.parse(pass.validFrom) > start) return false;
    if (pass.validUntil && Date.parse(pass.validUntil) <= start) return false;
    if (!pass.validFrom && Date.parse(pass.activationDeadline) <= start) return false;
    if (pass.rules.serviceUuids.length && (!serviceUuid || !pass.rules.serviceUuids.includes(serviceUuid))) return false;
    if (pass.rules.branchUuids.length) return false;
    const needed = pass.kind === "minutes" ? Math.ceil((end - start) / 60000) : 1;
    return pass.availableQuantity === null || pass.availableQuantity >= needed;
  }).sort((left, right) => Date.parse(left.validUntil ?? left.activationDeadline) - Date.parse(right.validUntil ?? right.activationDeadline));
}
